import { useEffect, useMemo, useState } from 'react';
import { network } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LayoutV2 from '../components/LayoutV2';
import {
  Badge,
  Button,
  Loading,
  PageHeader,
  SectionBlock
} from '../components/ui';

function getTierName(tier) {
  const tierNames = { 1: 'Director', 2: 'Manager', 3: 'Promoter' };
  return tierNames[tier] || '--';
}

export default function NetworkV2() {
  const { user, isAdmin, isOperations, isDirector, isManager, isPromoter } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parentInfo, setParentInfo] = useState(null);
  const [savingUserId, setSavingUserId] = useState('');
  const [tierDrafts, setTierDrafts] = useState({});
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      const summaryRes = await network.summary(params);
      setSummary(summaryRes.data || null);

      if (!isAdmin && !isOperations && user?.parentId) {
        try {
          const parentRes = await network.getUpline();
          setParentInfo(parentRes.data.user || null);
        } catch (err) {
          console.error('Failed to load parent info:', err);
        }
      }
    } catch (err) {
      console.error(err);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin || isOperations || isDirector || isManager) {
      loadData();
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (isAdmin || isOperations || isDirector || isManager) {
      loadData();
    }
  }, []);

  useEffect(() => {
    if (!isAdmin && !isOperations && user?.parentId) {
      const loadParent = async () => {
        try {
          const parentRes = await network.getUpline();
          setParentInfo(parentRes.data.user || null);
        } catch (err) {
          console.error('Failed to load parent info:', err);
        }
      };
      loadParent();
    }
  }, [user, isAdmin, isOperations]);

  const directDownlines = useMemo(() => summary?.directReports || [], [summary]);

  const handleToggleStatus = async (item) => {
    const nextStatus = item.status === 'active' ? 'disabled' : 'active';
    setSavingUserId(item.id);
    try {
      await network.manage(item.id, { status: nextStatus });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update user');
    } finally {
      setSavingUserId('');
    }
  };

  const handleTierChange = async (item) => {
    const nextTier = Number(tierDrafts[item.id]);
    if (!nextTier || nextTier === Number(item.tier)) return;
    setSavingUserId(item.id);
    try {
      await network.manage(item.id, { tier: nextTier });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update tier');
    } finally {
      setSavingUserId('');
    }
  };

  return (
    <LayoutV2>
      <PageHeader
        title="Network"
      />

      {loading ? (
        <Loading text="Loading network..." />
      ) : (
        <div className="space-y-6">
          {!isAdmin ? (
            <SectionBlock
              title="Your Upline"
            >
              {parentInfo ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="surface-card"><p className="label">Name</p><p className="text-sm text-slate-100">{parentInfo.display_name || '--'}</p></div>
                  <div className="surface-card"><p className="label">Username</p><p className="text-sm text-slate-100">@{parentInfo.username || '--'}</p></div>
                  <div className="surface-card"><p className="label">Level</p><p className="text-sm text-slate-100">{getTierName(parentInfo.tier)}</p></div>
                </div>
              ) : (
                <div className="rounded-[20px] border border-dashed border-white/10 bg-slate-950/20 p-6 text-sm text-slate-400">
                  No upline assigned.
                </div>
              )}
            </SectionBlock>
          ) : null}

          {isPromoter ? (
            <SectionBlock
              title="Growth Guide"
            >
              <div className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4 text-sm text-slate-300">
                To upgrade and grow your team, please contact your administrator and your direct upline for the next onboarding steps.
              </div>
            </SectionBlock>
          ) : null}

          {summary && (isAdmin || isOperations || isDirector || isManager) ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="surface-card"><p className="label">Promoters</p><p className="text-2xl font-semibold text-slate-50">{summary.summary?.totalPromoters || 0}</p></div>
              <div className="surface-card"><p className="label">Managers</p><p className="text-2xl font-semibold text-slate-50">{summary.summary?.totalManagers || 0}</p></div>
              <div className="surface-card"><p className="label">Loan Success Users</p><p className="text-2xl font-semibold text-slate-50">{summary.summary?.totalLoanSuccessUsers || 0}</p></div>
            </div>
          ) : null}

          {(isDirector || isManager) ? (
            <SectionBlock title="Time Range">
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  From:
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="input"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  To:
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="input"
                  />
                </label>
                {(fromDate || toDate) && (
                  <Button type="button" variant="secondary" size="sm" onClick={() => { setFromDate(''); setToDate(''); }}>
                    Clear
                  </Button>
                )}
              </div>
            </SectionBlock>
          ) : null}

          {(isDirector || isManager) && summary?.selfAndChildren ? (
            <SectionBlock title="Team Summary (Extended)">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="surface-card"><p className="label">Your Code</p><p className="font-mono text-slate-50">{summary.selfAndChildren.inviteCode || '--'}</p></div>
                <div className="surface-card"><p className="label">Team Orders</p><p className="text-2xl font-semibold text-slate-50">{summary.selfAndChildren.orderCount || 0}</p></div>
                <div className="surface-card"><p className="label">Team Volume</p><p className="text-2xl font-semibold text-slate-50">{summary.selfAndChildren.totalVolume || 0}</p></div>
                <div className="surface-card"><p className="label">Loan Success Users</p><p className="text-2xl font-semibold text-slate-50">{summary.selfAndChildren.totalLoanSuccessUsers || 0}</p></div>
              </div>
            </SectionBlock>
          ) : null}

          {(isAdmin || isOperations || isDirector || isManager) ? (
            <SectionBlock
              title="Direct Downlines"
            >
              {directDownlines.length === 0 ? (
                <div className="rounded-[20px] border border-dashed border-white/10 bg-slate-950/20 p-6 text-sm text-slate-400">
                  No direct downline found.
                </div>
              ) : (
                <>
                  <div className="hidden lg:block table-container">
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Username</th>
                            <th>Level</th>
                            <th>Referral Code</th>
                            <th>Team invited users</th>
                            <th>Loan Success</th>
                            <th>Join Time</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {directDownlines.map((item) => (
                            <tr key={item.id}>
                              <td>{item.display_name || '--'}</td>
                              <td>@{item.username || '--'}</td>
                              <td>
                                {isAdmin || isOperations ? (
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={tierDrafts[item.id] ?? item.tier}
                                      onChange={(e) => setTierDrafts((prev) => ({ ...prev, [item.id]: Number(e.target.value) }))}
                                      className="input min-w-[140px]"
                                      disabled={savingUserId === item.id}
                                    >
                                      <option value={1}>Director</option>
                                      <option value={2}>Manager</option>
                                      <option value={3}>Promoter</option>
                                      <option value={4}>Operations</option>
                                    </select>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="secondary"
                                      disabled={savingUserId === item.id || Number(tierDrafts[item.id] ?? item.tier) === Number(item.tier)}
                                      onClick={() => handleTierChange(item)}
                                    >
                                      Save
                                    </Button>
                                  </div>
                                ) : (
                                  getTierName(item.tier)
                                )}
                              </td>
                              <td>
                                <div className="font-mono">{item.inviteCode || '--'}</div>
                                {(item.gcash_name || item.gcash_number) ? (
                                  <div className="mt-1 text-xs text-slate-400">
                                    {item.gcash_name || '--'} {item.gcash_number ? `(${item.gcash_number})` : ''}
                                  </div>
                                ) : null}
                              </td>
                              <td>{item.inviteCodeUsageCount || 0}</td>
                              <td>{item.loanSuccessCount || 0}</td>
                              <td>{item.created_at ? new Date(item.created_at).toLocaleString() : '--'}</td>
                              <td>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  disabled={savingUserId === item.id}
                                  onClick={() => handleToggleStatus(item)}
                                >
                                  {item.status === 'active' ? 'Disable' : 'Enable'}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 lg:hidden">
                    {directDownlines.map((item) => (
                      <div key={item.id} className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-100">{item.display_name || '--'}</p>
                            <p className="mt-1 text-sm text-slate-400">@{item.username || '--'}</p>
                          </div>
                          <Badge variant="primary">{getTierName(item.tier)}</Badge>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Referral code</p>
                            <p className="mt-1 font-mono text-slate-200">{item.inviteCode || '--'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Team invited users</p>
                            <p className="mt-1 text-slate-200">{item.inviteCodeUsageCount || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Loan Success</p>
                            <p className="mt-1 text-slate-200">{item.loanSuccessCount || 0}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-slate-500">Join time</p>
                            <p className="mt-1 text-slate-200">{item.created_at ? new Date(item.created_at).toLocaleString() : '--'}</p>
                          </div>
                        </div>

                        {(item.gcash_name || item.gcash_number) ? (
                          <div className="mt-4 rounded-[16px] border border-white/8 bg-slate-950/30 p-3 text-sm text-slate-300">
                            {item.gcash_name || '--'} {item.gcash_number ? `(${item.gcash_number})` : ''}
                          </div>
                        ) : null}

                        <div className="mt-4 flex flex-wrap gap-2">
                          {isAdmin || isOperations ? (
                            <>
                              <select
                                value={tierDrafts[item.id] ?? item.tier}
                                onChange={(e) => setTierDrafts((prev) => ({ ...prev, [item.id]: Number(e.target.value) }))}
                                className="input flex-1"
                                disabled={savingUserId === item.id}
                              >
                                <option value={1}>Director</option>
                                <option value={2}>Manager</option>
                                <option value={3}>Promoter</option>
                                <option value={4}>Operations</option>
                              </select>
                              <Button
                                type="button"
                                variant="secondary"
                                disabled={savingUserId === item.id || Number(tierDrafts[item.id] ?? item.tier) === Number(item.tier)}
                                onClick={() => handleTierChange(item)}
                              >
                                Save Tier
                              </Button>
                            </>
                          ) : null}
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={savingUserId === item.id}
                            onClick={() => handleToggleStatus(item)}
                          >
                            {item.status === 'active' ? 'Disable' : 'Enable'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </SectionBlock>
          ) : null}
        </div>
      )}
    </LayoutV2>
  );
}
